import { activityHeaders, reportActivityError, trackActivity } from './activity'

const RETRY_DELAYS = [500, 1000]
const RETRY_STATUSES = new Set([502, 503, 504])
const ROUTES = new Set(['products', 'calendar-availability', 'availability', 'booking-status', 'quote', 'checkout-session', 'cancel-checkout', 'address-lookup', 'booking-details'])

// Report route templates only: no query strings, tokens, or customer values.
function requestRoute(url: string): string {
    try {
        const path = new URL(url).pathname.split('/v1/').at(-1) || ''
        if (/^products\/[^/]+$/.test(path)) return '/v1/products/:slug'
        if (ROUTES.has(path)) return `/v1/${path}`
    } catch { /* malformed URLs still produce a useful network diagnostic */ }
    return 'unknown-endpoint'
}

function waitForRetry(ms: number, signal?: AbortSignal | null): Promise<void> {
    return new Promise((resolve, reject) => {
        const abort = () => {
            clearTimeout(timer)
            reject(signal?.reason ?? new DOMException('Request cancelled', 'AbortError'))
        }
        const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve() }, ms)
        if (signal?.aborted) abort()
        else signal?.addEventListener('abort', abort, { once: true })
    })
}

export async function jsonFetch<T>(url: string, init: RequestInit = {}): Promise<T> {
    const method = (init.method || 'GET').toUpperCase()
    const endpoint = requestRoute(url)
    // Only these reads are safe to repeat. Checkout/quote/details writes and
    // booking-status polling retain their existing single-request behaviour.
    const retryable = (method === 'GET' && endpoint.startsWith('/v1/products'))
        || (method === 'POST' && endpoint === '/v1/calendar-availability')
    const maxAttempts = retryable ? RETRY_DELAYS.length + 1 : 1
    const headers = new Headers(activityHeaders())
    new Headers(init.headers).forEach((value, name) => headers.set(name, value))

    for (let attempt = 1;;attempt++) {
        init.signal?.throwIfAborted()
        const context = () => ({ endpoint, method, attempts: attempt, online: typeof navigator === 'undefined' ? undefined : navigator.onLine })
        let response: Response
        try {
            response = await fetch(url, { ...init, headers })
        } catch (reason) {
            if (init.signal?.aborted || (reason as Error)?.name === 'AbortError') throw reason
            if (attempt < maxAttempts) {
                await waitForRetry(RETRY_DELAYS[attempt - 1], init.signal)
                continue
            }
            reportActivityError('API_NETWORK_ERROR', reason, undefined, context())
            throw Object.assign(new Error('We couldn’t connect to the booking service. Check your connection and try again.'), { activityReported: true })
        }
        const payload = await response.json().catch(() => ({}))
        init.signal?.throwIfAborted()
        if (response.ok) return payload as T
        if (RETRY_STATUSES.has(response.status) && attempt < maxAttempts) {
            await waitForRetry(RETRY_DELAYS[attempt - 1], init.signal)
            continue
        }
        const error = Object.assign(new Error(payload?.error?.message || 'Something went wrong. Please try again.'), {
            code: payload?.error?.code,
            activityReported: true,
        })
        if (response.status >= 500) reportActivityError(error.code || 'API_ERROR', error, payload?.error?.requestId || response.headers.get('X-Request-ID') || undefined, { ...context(), status: response.status })
        else trackActivity('validation_failed', { code: error.code || 'REQUEST_REJECTED' })
        throw error
    }
}
